import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, LinearProgress, Switch, FormControlLabel, Tooltip, TableSortLabel,
  Collapse, Divider, CircularProgress, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import {
  Inventory, TrendingDown, Add, Edit, History, Warning,
  CheckCircle, Error, Refresh, AccountTree, ExpandMore, ExpandLess,
  RemoveCircle, Build
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { Pagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import usePageTitle from '../../hooks/usePageTitle';


function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function InventoryManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [stockLevels, setStockLevels] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustmentDialog, setAdjustmentDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Search text shared across the three tabs. TabPanel unmounts inactive
  // tabs, so each table re-seeds its search from here on mount - searching
  // on one tab carries the term (and results) to the others.
  const [sharedSearch, setSharedSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);

  // Stats for dashboard cards
  const [stats, setStats] = useState({
    total_items: 0,        
    low_stock_items: 0,    
    total_value: 0,        
    recent_transactions: 0
  });

  usePageTitle('Inventory');

  useEffect(() => {
    loadData();
  }, []);

  

  const loadData = async () => {
    setLoading(true);
    try {
      // Get stats separately - this is fast
      const statsResponse = await manufacturingAPI.stockLevels.inventoryStats();
      console.log('Stats response:', statsResponse.data);
      setStats(statsResponse.data);

      // Only load first page of stock levels (25 items)
      const stockResponse = await manufacturingAPI.stockLevels.list({ page_size: 25 });
      setStockLevels(stockResponse.data.results || []);

      // Only load low stock items that we'll display (limited)
      const lowStockResponse = await manufacturingAPI.stockLevels.list({ 
        low_stock: true, 
        page_size: 10 
      });
      setLowStockItems(lowStockResponse.data.results || []);

      // Only load recent transactions (limited)
      const transactionsResponse = await manufacturingAPI.stockTransactions.list({ 
        page_size: 50 
      });
      setTransactions(transactionsResponse.data.results || []);

    } catch (error) {
      console.error('Error loading inventory data:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const getStockStatusColor = (item) => {
    if (item.quantity_on_hand <= 0) return 'error';
    if (item.quantity_on_hand <= item.item.reorder_level) return 'warning';
    return 'success';
  };

  const getStockStatusText = (item) => {
    if (item.quantity_on_hand <= 0) return 'Out of Stock';
    if (item.quantity_on_hand <= item.item.reorder_level) return 'Low Stock';
    return 'In Stock';
  };

  const handleStockAdjustment = async (adjustmentData) => {
    try {
        console.log('Sending adjustment data:', adjustmentData);
        console.log('Selected item:', selectedItem);
        
        const payload = {
        item_id: selectedItem.item.id,  // Changed from 'item' to 'item_id'
        transaction_type: 'adjustment',
        quantity: parseFloat(adjustmentData.quantity), // Ensure it's a number
        unit_cost: parseFloat(selectedItem.item.standard_cost || 0),
        notes: adjustmentData.reason || '',
        };
        
        console.log('API payload:', payload);
        
        // Create stock transaction
        const response = await manufacturingAPI.stockTransactions.create(payload);
        
        console.log('Success response:', response);
        toast.success('Stock adjustment created successfully');
        
        // Reload data
        await loadData();
        setAdjustmentDialog(false);
        setSelectedItem(null);
    } catch (error) {
        console.error('Error creating stock adjustment:', error);
        console.error('Error response:', error.response?.data);
        
        // Show detailed error message
        let errorMessage = 'Failed to create stock adjustment';
        if (error.response?.data) {
        if (typeof error.response.data === 'string') {
            errorMessage += `: ${error.response.data}`;
        } else if (error.response.data.detail) {
            errorMessage += `: ${error.response.data.detail}`;
        } else if (error.response.data.non_field_errors) {
            errorMessage += `: ${error.response.data.non_field_errors.join(', ')}`;
        } else {
            // Show field-specific errors
            const fieldErrors = Object.entries(error.response.data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
            errorMessage += `: ${fieldErrors}`;
        }
        }
        
        toast.error(errorMessage);
    }
    };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Inventory Management</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory color="primary" />
          Inventory Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Items</Typography>
              <Typography variant="h4">{stats.total_items || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Low Stock Items</Typography>
              <Typography variant="h4" color="warning.main">{stats.low_stock_items || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Value</Typography>
              <Typography variant="h4">
                £{(stats.total_value || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Recent Transactions</Typography>
              <Typography variant="h4">{stats.recent_transactions || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{lowStockItems.length} items</strong> are below reorder level and need attention!
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Stock Levels" />
          <Tab label="Low Stock Alerts" />
          <Tab label="Transaction History" />
        </Tabs>

        {/* Stock Levels Tab */}
        <TabPanel value={tabValue} index={0}>
          <StockLevelsTable
            stockLevels={stockLevels}
            onAdjustStock={(item) => {
              setSelectedItem(item);
              setAdjustmentDialog(true);
            }}
            getStockStatusColor={getStockStatusColor}
            getStockStatusText={getStockStatusText}
            sharedSearch={sharedSearch}
            onSharedSearch={setSharedSearch}
          />
        </TabPanel>

        {/* UPDATED: Low Stock Tab with enhanced component */}
        <TabPanel value={tabValue} index={1}>
          <LowStockTable
            onAdjustStock={(item) => {
              setSelectedItem(item);
              setAdjustmentDialog(true);
            }}
            sharedSearch={sharedSearch}
            onSharedSearch={setSharedSearch}
          />
        </TabPanel>

        {/* UPDATED: Transactions Tab with enhanced component */}
        <TabPanel value={tabValue} index={2}>
          <TransactionsTable sharedSearch={sharedSearch} onSharedSearch={setSharedSearch} />
        </TabPanel>
      </Paper>
      

      {/* Stock Adjustment Dialog */}
      <StockAdjustmentDialog
        open={adjustmentDialog}
        onClose={() => setAdjustmentDialog(false)}
        item={selectedItem}
        onSubmit={handleStockAdjustment}
      />
    </Box>
  );
}

// Rectify Shortage/Surplus Dialog
function RectifyDialog({ open, onClose, detail, comp, onComplete }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // For shortage
  const [shortageQty, setShortageQty] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [dueDate, setDueDate] = useState('');
  // For surplus - smart reduction plan from backend
  const [reductionPlan, setReductionPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // Use BOM explosion values (comp) as authoritative
  const bomShortage = comp?.shortage || 0;
  const isShortage = bomShortage > 0;
  const isSurplus = bomShortage < 0;
  const surplusAmount = isSurplus ? Math.abs(bomShortage) : 0;

  // Initialize on open
  React.useEffect(() => {
    if (open && detail) {
      setResult(null);
      setError(null);
      setSubmitting(false);
      setReductionPlan(null);
      if (isShortage) {
        setShortageQty(String(Math.ceil(bomShortage)));
        setOrderRef('');
        setDueDate('');
      }
      if (isSurplus) {
        // Load smart reduction plan from backend
        loadReductionPlan();
      }
    }
  }, [open, detail]);

  const loadReductionPlan = async () => {
    setPlanLoading(true);
    try {
      const resp = await manufacturingAPI.stockLevels.reduceSurplus({
        item_id: comp.item.id,
        reduce_by: surplusAmount,
        preview: true,
      });
      setReductionPlan(resp.data);
    } catch (err) {
      console.error('Error loading reduction plan:', err);
      setError('Failed to calculate reduction plan');
    } finally {
      setPlanLoading(false);
    }
  };

  const handleCreateWOs = async () => {
    const qty = parseFloat(shortageQty);
    if (isNaN(qty) || qty <= 0) { setError('Enter a valid quantity'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const response = await manufacturingAPI.workOrders.createFromBomExplosion({
        item_id: comp.item.id,
        quantity: qty,
        customer_order_ref: orderRef || 'BOM Explosion Rectify',
        due_date: dueDate || null,
      });
      setResult(response.data);
    } catch (err) {
      console.error('Error creating work orders:', err);
      if (err.response?.data?.items_missing_operation) {
        setError({ type: 'missing_operations', message: err.response.data.message, items: err.response.data.items_missing_operation });
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create work orders');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReduceSurplus = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const resp = await manufacturingAPI.stockLevels.reduceSurplus({
        item_id: comp.item.id,
        reduce_by: surplusAmount,
        preview: false,
      });
      setResult({ type: 'surplus_reduced', ...resp.data });
    } catch (err) {
      console.error('Error reducing surplus:', err);
      setError('Failed to reduce work orders');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (result) onComplete();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{
        bgcolor: isShortage ? 'error.main' : 'success.main',
        color: 'white',
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        {isShortage ? <Add /> : <RemoveCircle />}
        {isShortage ? 'Rectify Shortage' : 'Rectify Surplus'}: {comp?.item?.item_code} - {comp?.item?.name}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {!result ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Summary section */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Item Summary</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="text.secondary">Required for Orders</Typography>
                  <Typography variant="h6">{comp?.quantity_required_for_orders || 0}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="text.secondary">Stock Available</Typography>
                  <Typography variant="h6">{comp?.quantity_available}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="text.secondary">In Production</Typography>
                  <Typography variant="h6" color="info.main">{comp?.quantity_in_production || 0}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="text.secondary">On Hand</Typography>
                  <Typography variant="h6">{comp?.quantity_on_hand}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="text.secondary">{isShortage ? 'Shortage' : 'Surplus'}</Typography>
                  <Typography variant="h6" color={isShortage ? 'error.main' : 'success.main'}>
                    {isShortage ? bomShortage : `+${surplusAmount}`}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Surplus: smart reduction plan */}
            {isSurplus && (
              planLoading ? (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>Calculating reduction plan (latest due dates first)...</Typography>
                </Box>
              ) : reductionPlan ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Reduction Plan (latest due dates reduced first)
                  </Typography>
                  <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    Reducing {reductionPlan.actual_reduction} of {surplusAmount} surplus.
                    Orders with the latest due dates are reduced first to keep urgent work intact.
                    {reductionPlan.shortfall > 0 && ` Cannot reduce ${reductionPlan.shortfall} - already completed or no work orders.`}
                  </Alert>

                  {reductionPlan.work_order_changes.map((woChange) => (
                    <Paper key={woChange.wo_id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography fontWeight="medium">{woChange.work_order_number}</Typography>
                        <Chip label={woChange.status} size="small"
                          color={woChange.status === 'in_progress' ? 'info' : woChange.status === 'released' ? 'primary' : 'default'} />
                        <Typography variant="body2">
                          <span style={{ textDecoration: 'line-through', color: '#999' }}>{woChange.original_qty}</span>
                          {' → '}
                          <strong style={{ color: '#2e7d32' }}>{woChange.new_qty}</strong>
                          {' '}(−{woChange.reduce_by})
                        </Typography>
                      </Box>

                      {/* Per-order breakdown of what's being reduced */}
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Customer Order</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell align="right">Original Qty</TableCell>
                            <TableCell align="right">Reduce By</TableCell>
                            <TableCell align="right">New Qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {woChange.breakdown_changes.map((bc, i) => (
                            <TableRow key={i} sx={{ bgcolor: bc.reduce_by > 0 ? 'error.50' : undefined }}>
                              <TableCell>{bc.order_number}</TableCell>
                              <TableCell>{bc.customer_name}</TableCell>
                              <TableCell>{bc.due_date_display}</TableCell>
                              <TableCell align="right">{bc.original_breakdown_qty}</TableCell>
                              <TableCell align="right">
                                {bc.reduce_by > 0 && (
                                  <Typography color="error.main" fontWeight="bold">−{bc.reduce_by}</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="bold">{bc.new_breakdown_qty}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  ))}

                  {reductionPlan.work_order_changes.length === 0 && (
                    <Alert severity="warning">No active work orders with demand breakdowns to reduce.</Alert>
                  )}
                </Paper>
              ) : null
            )}

            {/* Shortage: creation form */}
            {isShortage && (
              <>
                {/* Related customer orders */}
                {((detail?.direct_demand?.length > 0) || (detail?.indirect_demand?.length > 0)) && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Related Customer Orders</Typography>
                    {detail.direct_demand.length > 0 && (
                      <TableContainer sx={{ mb: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order</TableCell>
                              <TableCell>Customer</TableCell>
                              <TableCell align="right">Ordered</TableCell>
                              <TableCell align="right">Allocated</TableCell>
                              <TableCell align="right">Unallocated</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.direct_demand.map((d, i) => (
                              <TableRow key={i}>
                                <TableCell>
                                  <Typography variant="body2">{d.order_number}</Typography>
                                  {d.customer_reference && <Typography variant="caption" color="text.secondary">{d.customer_reference}</Typography>}
                                </TableCell>
                                <TableCell>{d.customer_name}</TableCell>
                                <TableCell align="right">{d.quantity_ordered}</TableCell>
                                <TableCell align="right">{d.quantity_allocated}</TableCell>
                                <TableCell align="right">
                                  <Typography color={d.quantity_unallocated > 0 ? 'error.main' : 'success.main'} fontWeight="bold">{d.quantity_unallocated}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {detail.indirect_demand.length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Also needed as component of:</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead><TableRow><TableCell>Order</TableCell><TableCell>Parent Item</TableCell><TableCell align="right">Needed</TableCell></TableRow></TableHead>
                            <TableBody>
                              {detail.indirect_demand.map((d, i) => (
                                <TableRow key={i}>
                                  <TableCell>{d.order_number}</TableCell>
                                  <TableCell>{d.parent_item_code} - {d.parent_item_name}</TableCell>
                                  <TableCell align="right"><Typography fontWeight="bold">{d.component_qty_needed}</Typography></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <Build fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Create Work Orders
                  </Typography>
                  <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    This will run a BOM explosion for {comp?.item?.item_code}, check existing stock at each level,
                    and create work orders for any components that need manufacturing.
                    {detail?.work_orders?.length > 0 && ' Existing planned/active work orders may be consolidated (quantity added).'}
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField label="Quantity" type="number" value={shortageQty} onChange={(e) => setShortageQty(e.target.value)} sx={{ width: 150 }} inputProps={{ min: 1 }} required />
                    <TextField label="Order Reference" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} placeholder="e.g., 12345" helperText="Displayed on terminals" sx={{ width: 200 }} />
                    <TextField label="Due Date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} sx={{ width: 180 }} InputLabelProps={{ shrink: true }} />
                  </Box>
                </Paper>
              </>
            )}

            {/* Error display */}
            {error && (
              <Alert severity="error">
                {typeof error === 'string' ? error : (
                  <>
                    <Typography variant="subtitle2">{error.message}</Typography>
                    {error.items && (
                      <List dense>
                        {error.items.map((item, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}><Warning color="error" fontSize="small" /></ListItemIcon>
                            <ListItemText primary={`${item.item_code} - ${item.item_name}`} secondary={`Needs ${item.quantity_needed} units`} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </Alert>
            )}
          </Box>
        ) : (
          /* Result display */
          <Box sx={{ mt: 1 }}>
            {result.type === 'surplus_reduced' ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Successfully reduced {result.actual_reduction} units across {result.work_order_changes?.length || 0} work order(s)
                </Alert>
                {result.work_order_changes?.map((woChange, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle color="success" fontSize="small" />
                      <Typography fontWeight="medium">{woChange.work_order_number}</Typography>
                      <Typography variant="body2">{woChange.original_qty} → {woChange.new_qty} (−{woChange.reduce_by})</Typography>
                    </Box>
                    {woChange.breakdown_changes?.map((bc, j) => (
                      bc.reduce_by > 0 && (
                        <Typography key={j} variant="body2" sx={{ pl: 4, color: 'text.secondary' }}>
                          {bc.order_number} ({bc.due_date_display}): {bc.original_breakdown_qty} → {bc.new_breakdown_qty}
                        </Typography>
                      )
                    ))}
                  </Box>
                ))}
              </>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}><Typography variant="subtitle2">{result.message}</Typography></Alert>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`${result.created || 0} Created`} color="success" icon={<Add />} />
                  <Chip label={`${result.consolidated || 0} Consolidated`} color="info" />
                  {result.multi_step > 0 && <Chip label={`${result.multi_step} Multi-step`} variant="outlined" />}
                </Box>
                <Divider sx={{ my: 1 }} />
                <List dense>
                  {result.work_orders?.map((wo, idx) => (
                    <ListItem key={idx} sx={{ bgcolor: wo.action === 'created' ? 'success.50' : 'info.50', mb: 0.5, borderRadius: 1 }}>
                      <ListItemIcon><CheckCircle color={wo.action === 'created' ? 'success' : 'info'} /></ListItemIcon>
                      <ListItemText
                        primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography fontWeight="medium">{wo.work_order_number}</Typography>
                          <Chip label={wo.action} size="small" color={wo.action === 'created' ? 'success' : 'info'} />
                          {wo.total_steps > 1 && <Chip label={`Step ${wo.step_number}/${wo.total_steps}`} size="small" variant="outlined" />}
                        </Box>}
                        secondary={`${wo.item_code} - ${wo.item_name} | Qty: ${wo.quantity} | Op: ${wo.operation}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!result ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {isShortage && (
              <Button variant="contained" color="error"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Add />}
                onClick={handleCreateWOs} disabled={submitting || !shortageQty}>
                {submitting ? 'Creating...' : 'Create Work Orders'}
              </Button>
            )}
            {isSurplus && reductionPlan?.actual_reduction > 0 && (
              <Button variant="contained" color="success"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <RemoveCircle />}
                onClick={handleReduceSurplus} disabled={submitting}>
                {submitting ? 'Reducing...' : `Reduce Work Orders (−${reductionPlan.actual_reduction})`}
              </Button>
            )}
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Rectify All Dialog - processes all shortages and surpluses across the BOM
function RectifyAllDialog({ open, onClose, bomLevels, bomRootItem }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState({ shortages: [], surpluses: [] });
  const [surplusDetails, setSurplusDetails] = useState({}); // keyed by item id - now holds reduction plans
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, label: '' });
  const [result, setResult] = useState(null);

  // On open, collect all items with non-zero shortage and load WO data for surpluses
  React.useEffect(() => {
    if (!open || !bomLevels?.length) return;

    const load = async () => {
      setLoading(true);
      setResult(null);
      setProgress({ current: 0, total: 0, label: '' });

      const shortages = [];
      const surpluses = [];

      // Collect from all levels
      for (const level of bomLevels) {
        for (const comp of level.items) {
          const s = comp.shortage || 0;
          if (s > 0) shortages.push(comp);
          else if (s < 0) surpluses.push(comp);
        }
      }

      // Load smart reduction plans for surplus items
      const details = {};
      for (const comp of surpluses) {
        try {
          const resp = await manufacturingAPI.stockLevels.reduceSurplus({
            item_id: comp.item.id,
            reduce_by: Math.abs(comp.shortage),
            preview: true,
          });
          details[comp.item.id] = resp.data;
        } catch (err) {
          console.error(`Error loading plan for ${comp.item.item_code}:`, err);
        }
      }

      setSurplusDetails(details);
      setItems({ shortages, surpluses });
      setLoading(false);
    };

    load();
  }, [open, bomLevels]);

  const handleSubmit = async () => {
    setSubmitting(true);
    const results = { shortagesCreated: [], surplusesReduced: [], errors: [] };
    const totalOps = items.shortages.length + items.surpluses.length;
    let completed = 0;

    // Process shortages - create WOs via BOM explosion
    for (const comp of items.shortages) {
      setProgress({ current: completed, total: totalOps, label: `Creating WOs for ${comp.item.item_code}...` });
      try {
        const resp = await manufacturingAPI.workOrders.createFromBomExplosion({
          item_id: comp.item.id,
          quantity: Math.ceil(comp.shortage),
          customer_order_ref: 'BOM Rectify All',
        });
        results.shortagesCreated.push({
          item_code: comp.item.item_code,
          item_name: comp.item.name,
          shortage: comp.shortage,
          wos_created: resp.data.created || 0,
          wos_consolidated: resp.data.consolidated || 0,
          work_orders: resp.data.work_orders || [],
        });
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.error || 'Failed';
        results.errors.push({ item_code: comp.item.item_code, type: 'shortage', error: msg });
      }
      completed++;
    }

    // Process surpluses - smart reduce via backend
    for (const comp of items.surpluses) {
      setProgress({ current: completed, total: totalOps, label: `Reducing WOs for ${comp.item.item_code}...` });
      const plan = surplusDetails[comp.item.id];
      if (!plan?.work_order_changes?.length) {
        completed++;
        continue;
      }

      try {
        const resp = await manufacturingAPI.stockLevels.reduceSurplus({
          item_id: comp.item.id,
          reduce_by: Math.abs(comp.shortage),
          preview: false,
        });
        const reduced = (resp.data.work_order_changes || []).map(wc => ({
          work_order_number: wc.work_order_number,
          old_qty: wc.original_qty,
          new_qty: wc.new_qty,
          reduced_by: wc.reduce_by,
        }));
        if (reduced.length > 0) {
          results.surplusesReduced.push({
            item_code: comp.item.item_code,
            item_name: comp.item.name,
            surplus: Math.abs(comp.shortage),
            reductions: reduced,
          });
        }
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to reduce';
        results.errors.push({ item_code: comp.item.item_code, type: 'surplus', error: msg });
      }
      completed++;
    }

    setProgress({ current: totalOps, total: totalOps, label: 'Complete' });
    setResult(results);
    setSubmitting(false);
  };

  const totalShortageItems = items.shortages.length;
  const totalSurplusItems = items.surpluses.length;
  const totalSurplusReduction = items.surpluses.reduce((sum, comp) => {
    const plan = surplusDetails[comp.item.id];
    return sum + (plan?.actual_reduction || 0);
  }, 0);

  return (
    <Dialog open={open} onClose={result ? () => { onClose(true); } : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Build />
        Rectify All - {bomRootItem?.item_code} BOM
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading item details...</Typography>
          </Box>
        ) : !result ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Overview */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {totalShortageItems > 0 && (
                <Chip icon={<Add />} label={`${totalShortageItems} item${totalShortageItems !== 1 ? 's' : ''} with shortage`} color="error" />
              )}
              {totalSurplusItems > 0 && (
                <Chip icon={<RemoveCircle />} label={`${totalSurplusItems} item${totalSurplusItems !== 1 ? 's' : ''} with surplus`} color="success" />
              )}
              {totalShortageItems === 0 && totalSurplusItems === 0 && (
                <Alert severity="success" sx={{ width: '100%' }}>All items are balanced - nothing to rectify.</Alert>
              )}
            </Box>

            {/* Shortages section */}
            {totalShortageItems > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'error.main' }}>
                  <Add /> Shortages - Work Orders to Create
                </Typography>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                  For each item below, a BOM explosion will run - checking stock, creating new work orders or consolidating with existing ones.
                </Alert>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Required</TableCell>
                        <TableCell align="right">Available</TableCell>
                        <TableCell align="right">In Production</TableCell>
                        <TableCell align="right">Shortage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.shortages.map((comp) => (
                        <TableRow key={comp.item.id}>
                          <TableCell><Typography fontWeight="medium">{comp.item.item_code}</Typography></TableCell>
                          <TableCell>{comp.item.name}</TableCell>
                          <TableCell><Chip label={comp.item.item_type.replace('_', ' ')} size="small" variant="outlined" /></TableCell>
                          <TableCell align="right">{comp.quantity_required_for_orders}</TableCell>
                          <TableCell align="right">{comp.quantity_available}</TableCell>
                          <TableCell align="right">{comp.quantity_in_production}</TableCell>
                          <TableCell align="right"><Chip label={comp.shortage} color="error" size="small" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Surpluses section */}
            {totalSurplusItems > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'success.main' }}>
                  <RemoveCircle /> Surpluses - Work Orders to Reduce (latest due dates first)
                </Typography>
                {items.surpluses.map((comp) => {
                  const plan = surplusDetails[comp.item.id];
                  const surplus = Math.abs(comp.shortage);

                  return (
                    <Paper key={comp.item.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography fontWeight="medium">{comp.item.item_code}</Typography>
                        <Typography variant="body2" color="text.secondary">{comp.item.name}</Typography>
                        <Chip label={`Surplus: +${surplus}`} color="success" size="small" />
                        {plan && <Chip label={`Will reduce: ${plan.actual_reduction}`} size="small" color="primary" variant="outlined" />}
                      </Box>
                      {plan?.reduction_details?.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>WO Number</TableCell>
                              <TableCell>Customer Order</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell align="right">Order Qty</TableCell>
                              <TableCell align="right">Reduce By</TableCell>
                              <TableCell align="right">New Qty</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {plan.reduction_details.map((rd, i) => (
                              <TableRow key={i} sx={{ bgcolor: rd.reduce_by > 0 ? 'error.50' : undefined }}>
                                <TableCell>{rd.work_order_number}</TableCell>
                                <TableCell>{rd.order_number}</TableCell>
                                <TableCell>{rd.due_date_display}</TableCell>
                                <TableCell align="right">{rd.original_breakdown_qty}</TableCell>
                                <TableCell align="right"><Typography color="error.main" fontWeight="bold">-{rd.reduce_by}</Typography></TableCell>
                                <TableCell align="right"><Typography fontWeight="bold">{rd.new_breakdown_qty}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Alert severity="warning" variant="outlined">No active work orders with breakdowns to reduce.</Alert>
                      )}
                    </Paper>
                  );
                })}
              </Paper>
            )}

            {/* Progress during submit */}
            {submitting && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{progress.label}</Typography>
                <LinearProgress variant="determinate" value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0} />
              </Box>
            )}
          </Box>
        ) : (
          /* Results */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity={result.errors.length > 0 ? 'warning' : 'success'}>
              {result.errors.length > 0
                ? `Completed with ${result.errors.length} error(s)`
                : 'All items rectified successfully'}
            </Alert>

            {/* Shortage results */}
            {result.shortagesCreated.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Work Orders Created for Shortages</Typography>
                {result.shortagesCreated.map((item, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CheckCircle color="success" fontSize="small" />
                      <Typography fontWeight="medium">{item.item_code}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.item_name}</Typography>
                      <Chip label={`${item.wos_created} created`} size="small" color="success" />
                      {item.wos_consolidated > 0 && <Chip label={`${item.wos_consolidated} consolidated`} size="small" color="info" />}
                    </Box>
                    <List dense sx={{ pl: 2 }}>
                      {item.work_orders.map((wo, j) => (
                        <ListItem key={j} sx={{ py: 0 }}>
                          <ListItemText
                            primary={`${wo.work_order_number} - ${wo.item_code}`}
                            secondary={`Qty: ${wo.quantity} | ${wo.action}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Paper>
            )}

            {/* Surplus results */}
            {result.surplusesReduced.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Work Orders Reduced for Surpluses</Typography>
                {result.surplusesReduced.map((item, i) => (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CheckCircle color="success" fontSize="small" />
                      <Typography fontWeight="medium">{item.item_code}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.item_name}</Typography>
                    </Box>
                    {item.reductions.map((r, j) => (
                      <Typography key={j} variant="body2" sx={{ pl: 4 }}>
                        {r.work_order_number}: {r.old_qty} → {r.new_qty} (−{r.reduced_by})
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Paper>
            )}

            {/* Errors */}
            {result.errors.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>Errors</Typography>
                {result.errors.map((e, i) => (
                  <Alert key={i} severity="error" variant="outlined" sx={{ mb: 0.5 }}>
                    {e.item_code}: {e.error}
                  </Alert>
                ))}
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!result ? (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Build />}
              onClick={handleSubmit}
              disabled={submitting || (totalShortageItems === 0 && totalSurplusReduction === 0)}
            >
              {submitting ? 'Processing...' : `Rectify All (${totalShortageItems + totalSurplusItems} items)`}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={() => onClose(true)}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// BOM Item Row with expandable detail (order demand + work orders)
function BomItemRow({ comp, showParent, colSpan }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingWo, setEditingWo] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [rectifyOpen, setRectifyOpen] = useState(false);

  const loadDetail = async () => {
    if (detail) { setExpanded(!expanded); return; }
    setExpanded(true);
    setDetailLoading(true);
    try {
      const response = await manufacturingAPI.stockLevels.bomItemDetail({ item_id: comp.item.id });
      setDetail(response.data);
    } catch (err) {
      console.error('Error loading item detail:', err);
      toast.error('Failed to load item detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateWo = async (woId) => {
    const qty = parseFloat(editQty);
    if (isNaN(qty) || qty <= 0) { toast.error('Enter a valid quantity'); return; }
    try {
      await manufacturingAPI.workOrders.update(woId, { quantity_to_produce: qty });
      toast.success('Work order updated');
      setEditingWo(null);
      setEditQty('');
      // Reload detail
      const refreshed = await manufacturingAPI.stockLevels.bomItemDetail({ item_id: comp.item.id });
      setDetail(refreshed.data);
    } catch (err) {
      console.error('Error updating work order:', err);
      toast.error('Failed to update work order');
    }
  };

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'none' : undefined } }}
        onClick={loadDetail}
      >
        <TableCell padding="checkbox">
          <IconButton size="small">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">{comp.item.item_code}</Typography>
        </TableCell>
        <TableCell>{comp.item.name}</TableCell>
        <TableCell>
          <Chip label={comp.item.item_type.replace('_', ' ')} size="small" variant="outlined" />
        </TableCell>
        {showParent && <TableCell>{comp.parent_item_code || '-'}</TableCell>}
        {showParent && (
          <TableCell align="right">
            {comp.bom_quantity != null ? parseFloat(comp.bom_quantity).toFixed(4) : '-'}
          </TableCell>
        )}
        <TableCell align="right">{comp.quantity_on_hand}</TableCell>
        <TableCell align="right">{comp.quantity_allocated_to_orders || 0}</TableCell>
        <TableCell align="right">
          <Typography color={(comp.quantity_in_production || 0) > 0 ? 'info.main' : 'text.secondary'}>
            {comp.quantity_in_production || 0}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography fontWeight={(comp.quantity_required_for_orders || 0) > 0 ? 'bold' : 'normal'}>
            {comp.quantity_required_for_orders || 0}
          </Typography>
        </TableCell>
        <TableCell align="right">
          {(comp.shortage || 0) > 0 ? (
            <Chip label={comp.shortage} color="error" size="small" />
          ) : (comp.shortage || 0) < 0 ? (
            <Chip label={`+${Math.abs(comp.shortage)}`} color="success" size="small" />
          ) : (
            <Typography color="text.secondary">0</Typography>
          )}
        </TableCell>
        <TableCell align="right">{comp.quantity_available}</TableCell>
      </TableRow>

      {/* Expandable detail row */}
      <TableRow>
        <TableCell colSpan={colSpan + 1} sx={{ py: 0, px: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              {detailLoading ? (
                <LinearProgress />
              ) : detail ? (
                <Grid container spacing={2}>
                  {/* Summary cards */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={`Direct demand: ${detail.summary.total_direct_demand}`} variant="outlined" />
                      <Chip label={`Indirect demand: ${detail.summary.total_indirect_demand}`} variant="outlined" />
                      <Chip label={`Stock available: ${detail.summary.stock_available}`} color="primary" variant="outlined" />
                      <Chip label={`In production: ${detail.summary.total_in_production}`} color="info" variant="outlined" />
                      {detail.summary.gap > 0 ? (
                        <Chip label={`Shortage: ${detail.summary.gap}`} color="error" />
                      ) : detail.summary.gap < 0 ? (
                        <Chip label={`Surplus: +${Math.abs(detail.summary.gap)}`} color="success" />
                      ) : (
                        <Chip label="Balanced" color="success" />
                      )}
                    </Box>
                  </Grid>

                  {/* Direct demand - customer orders for this exact item */}
                  {detail.direct_demand.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Direct Orders ({detail.direct_demand.length})
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order</TableCell>
                              <TableCell>Customer</TableCell>
                              <TableCell align="right">Ordered</TableCell>
                              <TableCell align="right">Allocated</TableCell>
                              <TableCell align="right">Unallocated</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.direct_demand.map((d, i) => (
                              <TableRow key={i}>
                                <TableCell>
                                  <Typography variant="body2">{d.order_number}</Typography>
                                  <Typography variant="caption" color="text.secondary">{d.customer_reference}</Typography>
                                </TableCell>
                                <TableCell>{d.customer_name}</TableCell>
                                <TableCell align="right">{d.quantity_ordered}</TableCell>
                                <TableCell align="right">{d.quantity_allocated}</TableCell>
                                <TableCell align="right">
                                  {d.quantity_unallocated > 0 ? (
                                    <Typography color="error.main" fontWeight="bold">{d.quantity_unallocated}</Typography>
                                  ) : (
                                    <Typography color="success.main">0</Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}

                  {/* Indirect demand - needed as component of other ordered products */}
                  {detail.indirect_demand.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Indirect Demand (as BOM component) ({detail.indirect_demand.length})
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order</TableCell>
                              <TableCell>Parent Item</TableCell>
                              <TableCell align="right">Unalloc'd (parent)</TableCell>
                              <TableCell align="right">BOM Qty</TableCell>
                              <TableCell align="right">Needed</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.indirect_demand.map((d, i) => (
                              <TableRow key={i}>
                                <TableCell>
                                  <Typography variant="body2">{d.order_number}</Typography>
                                  <Typography variant="caption" color="text.secondary">{d.customer_name}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{d.parent_item_code}</Typography>
                                  <Typography variant="caption" color="text.secondary">{d.parent_item_name}</Typography>
                                </TableCell>
                                <TableCell align="right">{d.quantity_unallocated_parent}</TableCell>
                                <TableCell align="right">{d.bom_qty_per_parent}</TableCell>
                                <TableCell align="right">
                                  <Typography color="error.main" fontWeight="bold">{d.component_qty_needed}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}

                  {detail.direct_demand.length === 0 && detail.indirect_demand.length === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="info" variant="outlined">No active customer order demand for this item.</Alert>
                    </Grid>
                  )}

                  {/* Existing work orders */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Active Work Orders ({detail.work_orders.length})
                    </Typography>
                    {detail.work_orders.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>WO Number</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Customer Order</TableCell>
                              <TableCell align="right">Qty to Produce</TableCell>
                              <TableCell align="right">Completed</TableCell>
                              <TableCell align="right">Remaining</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.work_orders.map((wo) => (
                              <TableRow key={wo.id}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">{wo.work_order_number}</Typography>
                                  {wo.step_number > 1 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Step {wo.step_number}/{wo.total_steps}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={wo.status}
                                    size="small"
                                    color={wo.status === 'in_progress' ? 'info' : wo.status === 'released' ? 'primary' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>{wo.customer_order_number || wo.customer_order_ref || '-'}</TableCell>
                                <TableCell align="right">
                                  {editingWo === wo.id ? (
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={editQty}
                                      onChange={(e) => setEditQty(e.target.value)}
                                      sx={{ width: 100 }}
                                      onClick={(e) => e.stopPropagation()}
                                      autoFocus
                                    />
                                  ) : (
                                    wo.quantity_to_produce
                                  )}
                                </TableCell>
                                <TableCell align="right">{wo.quantity_completed}</TableCell>
                                <TableCell align="right">
                                  <Typography color={wo.quantity_remaining > 0 ? 'warning.main' : 'success.main'}>
                                    {wo.quantity_remaining}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {editingWo === wo.id ? (
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={(e) => { e.stopPropagation(); handleUpdateWo(wo.id); }}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); setEditingWo(null); setEditQty(''); }}
                                      >
                                        Cancel
                                      </Button>
                                    </Box>
                                  ) : (
                                    <Tooltip title="Edit quantity to produce">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingWo(wo.id);
                                          setEditQty(String(wo.quantity_to_produce));
                                        }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="warning" variant="outlined">No active work orders for this item.</Alert>
                    )}
                  </Grid>

                  {/* Rectify buttons - use comp.shortage from BOM explosion as authoritative */}
                  {(comp.shortage || 0) !== 0 && (
                    <Grid item xs={12}>
                      {comp.shortage > 0 ? (
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Add />}
                          onClick={(e) => { e.stopPropagation(); setRectifyOpen(true); }}
                        >
                          Rectify Shortage ({comp.shortage})
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<RemoveCircle />}
                          onClick={(e) => { e.stopPropagation(); setRectifyOpen(true); }}
                        >
                          Rectify Surplus (+{Math.abs(comp.shortage)})
                        </Button>
                      )}
                    </Grid>
                  )}
                </Grid>
              ) : null}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Rectify Dialog */}
      {detail && (
        <RectifyDialog
          open={rectifyOpen}
          onClose={() => setRectifyOpen(false)}
          detail={detail}
          comp={comp}
          onComplete={async () => {
            // Reload detail after changes
            try {
              const refreshed = await manufacturingAPI.stockLevels.bomItemDetail({ item_id: comp.item.id });
              setDetail(refreshed.data);
            } catch (err) {
              console.error('Error refreshing detail:', err);
            }
          }}
        />
      )}
    </>
  );
}

// Stock Levels Table Component
function StockLevelsTable({ stockLevels, onAdjustStock, getStockStatusColor, getStockStatusText, sharedSearch = '', onSharedSearch }) {
  const [localSearch, setLocalSearch] = useState(sharedSearch);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(stockLevels);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortDir, setSortDir] = useState(null); // null | 'asc' | 'desc'

  // Toggle for order requirements (expensive calculation)
  const [showOrderRequirements, setShowOrderRequirements] = useState(false);

  // BOM Explosion state
  const [bomExplosionEnabled, setBomExplosionEnabled] = useState(false);
  const [selectedItemForBom, setSelectedItemForBom] = useState(null);
  const [bomLevels, setBomLevels] = useState([]);
  const [bomVisibleLevels, setBomVisibleLevels] = useState(0);
  const [bomLoading, setBomLoading] = useState(false);
  const [bomRootItem, setBomRootItem] = useState(null);
  const bomRevealTimer = useRef(null);
  const [rectifyAllOpen, setRectifyAllOpen] = useState(false);

  useEffect(() => {
    // Seed the initial load (and requirement-toggle reloads) with the
    // current search so a term carried over from another tab applies.
    loadPage(1, localSearch, sortDir === 'asc' ? 'quantity_on_hand' : sortDir === 'desc' ? '-quantity_on_hand' : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderRequirements]);

  // Progressive reveal effect: when bomLevels changes, reveal levels one at a time
  useEffect(() => {
    if (bomLevels.length > 0 && bomVisibleLevels < bomLevels.length) {
      bomRevealTimer.current = setTimeout(() => {
        setBomVisibleLevels(prev => prev + 1);
      }, 300);
    }
    return () => {
      if (bomRevealTimer.current) clearTimeout(bomRevealTimer.current);
    };
  }, [bomLevels, bomVisibleLevels]);

  const handleBomExplosion = async (stock) => {
    if (!bomExplosionEnabled) return;

    setSelectedItemForBom(stock);
    setBomLevels([]);
    setBomVisibleLevels(0);
    setBomLoading(true);
    setBomRootItem(null);

    try {
      const response = await manufacturingAPI.stockLevels.bomItemExplosion({
        item_id: stock.item.id
      });
      const data = response.data;
      setBomRootItem(data.root_item);
      setBomLevels(data.levels || []);
      // Start progressive reveal from 0 - the useEffect will handle it
      setBomVisibleLevels(0);
      // Kick off first reveal
      setTimeout(() => setBomVisibleLevels(1), 100);
    } catch (error) {
      console.error('Error loading BOM explosion:', error);
      toast.error('Failed to load BOM explosion');
    } finally {
      setBomLoading(false);
    }
  };

  const handleBomToggle = (checked) => {
    setBomExplosionEnabled(checked);
    if (!checked) {
      setSelectedItemForBom(null);
      setBomLevels([]);
      setBomVisibleLevels(0);
      setBomRootItem(null);
    }
  };

  const loadPage = async (pageNum, search = '', ordering = null) => {
    setLoading(true);
    try {
      console.log(`Loading page ${pageNum} with search: ${search}, showRequirements: ${showOrderRequirements}`);

      if (showOrderRequirements) {
        // Use the heavy endpoint with BOM explosion
        const response = await manufacturingAPI.stockLevels.withOrderRequirements({
          page: pageNum,
          page_size: 5,
          search: search
        });

        console.log('Requirements response:', response.data);

        setData(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setTotalPages(response.data.total_pages || 1);
        setPage(pageNum);
      } else {
        const params = { page: pageNum, page_size: 25, search: search };
        if (ordering) params.ordering = ordering;
        const response = await manufacturingAPI.stockLevels.list(params);

        console.log('Response:', response.data);

        setData(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / 25));
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error loading stock levels:', error);
      toast.error('Failed to load stock levels');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSharedSearch) onSharedSearch(localSearch);
    loadPage(1, localSearch, sortDir === 'asc' ? 'quantity_on_hand' : sortDir === 'desc' ? '-quantity_on_hand' : null);
  };

  const handleSortOnHand = () => {
    const next = sortDir === null ? 'asc' : sortDir === 'asc' ? 'desc' : null;
    setSortDir(next);
    const ordering = next === 'asc' ? 'quantity_on_hand' : next === 'desc' ? '-quantity_on_hand' : null;
    loadPage(1, localSearch, ordering);
  };

  const pageSize = showOrderRequirements ? 5 : 25;

  return (
    <>
      {/* Search and Toggle Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box component="form" onSubmit={handleSearch} sx={{ flex: 1, minWidth: 200 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by item code or name..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton type="submit" size="small">
                  <SearchIcon />
                </IconButton>
              )
            }}
          />
        </Box>
        
        {/* Toggle for Order Requirements */}
        <FormControlLabel
          control={
            <Switch
              checked={showOrderRequirements}
              onChange={(e) => setShowOrderRequirements(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">Show Order Requirements</Typography>
              {showOrderRequirements && (
                <Chip label="Slow" size="small" color="warning" />
              )}
            </Box>
          }
        />

        {/* Toggle for BOM Explosion */}
        <FormControlLabel
          control={
            <Switch
              checked={bomExplosionEnabled}
              onChange={(e) => handleBomToggle(e.target.checked)}
              color="secondary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccountTree fontSize="small" />
              <Typography variant="body2">Explode BOM</Typography>
              {bomExplosionEnabled && (
                <Chip label="Slow" size="small" color="warning" />
              )}
            </Box>
          }
        />
      </Box>

      {/* Info Alert when toggle is on */}
      {showOrderRequirements && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Calculating requirements from all unfulfilled customer orders (BOM explosion).
          Showing {pageSize} items per page for performance.
        </Alert>
      )}

      {bomExplosionEnabled && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<AccountTree />}>
          Click any row to explode its BOM and see stock details for each component, level by level.
        </Alert>
      )}

      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip
                      title={sortDir === 'asc' ? 'Sorted: Low → High (click for High → Low)' : sortDir === 'desc' ? 'Sorted: High → Low (click to clear)' : 'Click to sort Low → High'}
                      placement="top"
                      arrow
                    >
                      <TableSortLabel
                        active={sortDir !== null}
                        direction={sortDir === 'desc' ? 'desc' : 'asc'}
                        onClick={handleSortOnHand}
                        sx={{ '& .MuiTableSortLabel-icon': { opacity: sortDir === null ? 0.3 : 1 } }}
                      >
                        On Hand
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell align="right">In Production</TableCell>
                  {showOrderRequirements && (
                    <>
                      <TableCell align="right">Required for Orders</TableCell>
                      <TableCell align="right">Shortage / Surplus</TableCell>
                    </>
                  )}
                  <TableCell align="right">Available</TableCell>
                  <TableCell align="right">Reorder Level</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((stock) => (
                  <TableRow
                    key={stock.id}
                    hover={bomExplosionEnabled}
                    selected={bomExplosionEnabled && selectedItemForBom?.item?.id === stock.item.id}
                    onClick={() => bomExplosionEnabled && handleBomExplosion(stock)}
                    sx={bomExplosionEnabled ? { cursor: 'pointer' } : {}}
                  >
                    <TableCell>{stock.item.item_code}</TableCell>
                    <TableCell>{stock.item.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={stock.item.item_type.replace('_', ' ')} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{stock.quantity_on_hand}</TableCell>
                    <TableCell align="right">
                      {stock.quantity_allocated_to_orders || 0}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        color={(stock.quantity_in_production || 0) > 0 ? 'info.main' : 'text.secondary'}
                      >
                        {stock.quantity_in_production || 0}
                      </Typography>
                    </TableCell>
                    {showOrderRequirements && (
                      <>
                        <TableCell align="right">
                          <Typography 
                            fontWeight={(stock.quantity_required_for_orders || 0) > 0 ? 'bold' : 'normal'}
                          >
                            {stock.quantity_required_for_orders || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {(stock.shortage || 0) > 0 ? (
                            <Chip
                              label={stock.shortage}
                              color="error"
                              size="small"
                            />
                          ) : (stock.shortage || 0) < 0 ? (
                            <Chip
                              label={`+${Math.abs(stock.shortage)}`}
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Typography color="text.secondary">0</Typography>
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell align="right">
                      {stock.quantity_available}
                    </TableCell>
                    <TableCell align="right">{stock.item.reorder_level}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStockStatusText(stock)}
                        color={getStockStatusColor(stock)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => onAdjustStock(stock)}
                        title="Adjust Stock"
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => loadPage(newPage, localSearch, sortDir === 'asc' ? 'quantity_on_hand' : sortDir === 'desc' ? '-quantity_on_hand' : null)}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* BOM Explosion Results */}
      {bomExplosionEnabled && (selectedItemForBom || bomLoading) && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTree color="secondary" />
              BOM Explosion: {bomRootItem ? `${bomRootItem.item_code} - ${bomRootItem.name}` : 'Loading...'}
            </Typography>
            {!bomLoading && bomVisibleLevels >= bomLevels.length && bomLevels.length > 1 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Build />}
                onClick={() => setRectifyAllOpen(true)}
              >
                Rectify All
              </Button>
            )}
          </Box>

          {bomLoading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Calculating BOM requirements...
              </Typography>
              <LinearProgress color="secondary" />
            </Box>
          )}

          {bomLevels.slice(0, bomVisibleLevels).map((level, levelIdx) => (
            <Collapse in key={level.level} timeout={400}>
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{
                  px: 2, py: 1,
                  bgcolor: level.level === 0 ? 'secondary.main' : 'grey.100',
                  color: level.level === 0 ? 'secondary.contrastText' : 'text.primary',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <AccountTree fontSize="small" />
                  <Typography variant="subtitle2">
                    {level.label}
                  </Typography>
                  <Chip
                    label={`${level.items.length} item${level.items.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={level.level === 0 ? { color: 'secondary.contrastText', borderColor: 'secondary.contrastText' } : {}}
                    variant="outlined"
                  />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Item Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        {level.level > 0 && <TableCell>Parent</TableCell>}
                        {level.level > 0 && <TableCell align="right">BOM Qty</TableCell>}
                        <TableCell align="right">On Hand</TableCell>
                        <TableCell align="right">Allocated</TableCell>
                        <TableCell align="right">In Production</TableCell>
                        <TableCell align="right">Required for Orders</TableCell>
                        <TableCell align="right">Shortage / Surplus</TableCell>
                        <TableCell align="right">Available</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {level.items.map((comp, idx) => (
                        <BomItemRow
                          key={`${comp.item.id}-${idx}`}
                          comp={comp}
                          showParent={level.level > 0}
                          colSpan={level.level > 0 ? 12 : 10}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Collapse>
          ))}

          {/* Progress bar while levels are still being revealed */}
          {!bomLoading && bomVisibleLevels < bomLevels.length && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading levels... ({bomVisibleLevels} of {bomLevels.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round((bomVisibleLevels / bomLevels.length) * 100)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(bomVisibleLevels / bomLevels.length) * 100}
                color="secondary"
              />
            </Box>
          )}

          {!bomLoading && bomVisibleLevels >= bomLevels.length && bomLevels.length > 0 && (
            <Alert severity="success" sx={{ mt: 1 }}>
              All {bomLevels.length} level{bomLevels.length !== 1 ? 's' : ''} loaded
              ({bomLevels.reduce((sum, l) => sum + l.items.length, 0)} total components)
            </Alert>
          )}

          {!bomLoading && bomLevels.length === 1 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This item has no BOM children.
            </Alert>
          )}
        </Box>
      )}

      {/* Rectify All Dialog */}
      <RectifyAllDialog
        open={rectifyAllOpen}
        onClose={(refresh) => {
          setRectifyAllOpen(false);
          if (refresh && selectedItemForBom) {
            handleBomExplosion(selectedItemForBom);
          }
        }}
        bomLevels={bomLevels}
        bomRootItem={bomRootItem}
      />
    </>
  );
}

// Low Stock Table Component
function LowStockTable({ onAdjustStock, sharedSearch = '', onSharedSearch }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(sharedSearch);
  const [localSearch, setLocalSearch] = useState(sharedSearch);

  const pageSize = 25;

  const loadLowStockData = async (pageNum, search = '') => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        page_size: pageSize,
        low_stock: true
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await manufacturingAPI.stockLevels.list(params);
      
      setData(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading low stock data:', error);
      toast.error('Failed to load low stock items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLowStockData(1, searchTerm);
  }, [searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSharedSearch) onSharedSearch(localSearch);
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    loadLowStockData(newPage, searchTerm);
  };

  return (
    <Box>
      {/* Search Bar */}
      <Box component="form" onSubmit={handleSearch} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search low stock items..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          InputProps={{
            endAdornment: (
              <IconButton type="submit" size="small">
                <SearchIcon />
              </IconButton>
            )
          }}
        />
      </Box>

      {/* Summary Alert */}
      {!loading && totalCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{totalCount} items</strong> are below reorder level and need attention!
          {searchTerm && ` (filtered results shown)`}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Current Stock</TableCell>
              <TableCell align="right">Reorder Level</TableCell>
              <TableCell align="right">Shortage</TableCell>
              <TableCell>Urgency</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((stock) => {
              const shortage = Math.max(0, stock.item.reorder_level - stock.quantity_on_hand);
              const urgency = stock.quantity_on_hand <= 0 ? 'Critical' : 'Low';
              
              return (
                <TableRow 
                  key={stock.id}
                  hover
                  sx={{
                    '& td': { 
                      color: 'text.primary',  // Add this to ensure text is visible
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {stock.item.item_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {stock.item.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stock.item.item_type.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2"
                      color={stock.quantity_on_hand <= 0 ? 'error.main' : 'warning.main'}
                      fontWeight="bold"
                    >
                      {stock.quantity_on_hand}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {stock.item.reorder_level}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      {shortage}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={urgency}
                      color={urgency === 'Critical' ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button 
                      size="small" 
                      variant="outlined"
                      color={urgency === 'Critical' ? 'error' : 'warning'}
                      onClick={() => onAdjustStock(stock)}
                    >
                      Adjust Stock
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {!loading && data.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            {searchTerm 
              ? 'No low stock items match your search' 
              : 'No low stock items found - all items are above reorder level!'
            }
          </Typography>
          {!searchTerm && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Great! All items are properly stocked.
            </Alert>
          )}
        </Box>
      )}

      {/* Pagination */}
      {Math.ceil(totalCount / pageSize) > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination 
            count={Math.ceil(totalCount / pageSize)} 
            page={page}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            disabled={loading}
          />
        </Box>
      )}
    </Box>
  );
}

// Transactions Table Component  
function TransactionsTable({ sharedSearch = '', onSharedSearch }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(sharedSearch);
  const [localSearch, setLocalSearch] = useState(sharedSearch);
  const [filterType, setFilterType] = useState('');
  const [dateRange, setDateRange] = useState('7'); // days

  const pageSize = 50; // More transactions per page since they're smaller

  const transactionTypes = [
    { value: '', label: 'All Types' },
    { value: 'receipt', label: 'Goods Received' },
    { value: 'issue', label: 'Issue to Production' },
    { value: 'production', label: 'Production Complete' },
    { value: 'adjustment', label: 'Stock Adjustment' },
    { value: 'scrap', label: 'Scrap/Write-off' }
  ];

  const loadTransactions = async (pageNum, search = '', type = '', days = '7') => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        page_size: pageSize
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (type) {
        params.transaction_type = type;
      }

      if (days && days !== 'all') {
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - parseInt(days));
        params.timestamp__gte = daysAgo.toISOString().split('T')[0];
      }

      const response = await manufacturingAPI.stockTransactions.list(params);
      
      setData(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions(1, searchTerm, filterType, dateRange);
  }, [searchTerm, filterType, dateRange]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSharedSearch) onSharedSearch(localSearch);
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    loadTransactions(newPage, searchTerm, filterType, dateRange);
  };

  const getTransactionIcon = (type) => {
    const icons = {
      receipt: <Add color="success" />,
      issue: <TrendingDown color="warning" />,
      production: <CheckCircle color="success" />,
      adjustment: <Edit color="info" />,
      scrap: <Error color="error" />
    };
    return icons[type] || <History />;
  };

  const formatTransactionType = (type) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box>
      {/* Filter Controls */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Box component="form" onSubmit={handleSearch}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search transactions..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" size="small">
                    <SearchIcon />
                  </IconButton>
                )
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Transaction Type</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Transaction Type"
            >
              {transactionTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Time Period</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Time Period"
            >
              <MenuItem value="1">Last 24 hours</MenuItem>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="all">All time</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Typography variant="body2" color="textSecondary" sx={{ pt: 1 }}>
            {totalCount.toLocaleString()} transactions
          </Typography>
        </Grid>
      </Grid>

      {/* Active Filters */}
      {(searchTerm || filterType || dateRange !== '7') && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Active filters:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                onDelete={() => {
                  setSearchTerm('');
                  setLocalSearch('');
                }}
                size="small"
              />
            )}
            {filterType && (
              <Chip
                label={`Type: ${transactionTypes.find(t => t.value === filterType)?.label}`}
                onDelete={() => setFilterType('')}
                size="small"
              />
            )}
            {dateRange !== '7' && (
              <Chip
                label={`Period: ${dateRange === 'all' ? 'All time' : `Last ${dateRange} days`}`}
                onDelete={() => setDateRange('7')}
                size="small"
              />
            )}
          </Box>
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Transaction #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Unit Cost</TableCell>
              <TableCell align="right">Total Value</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((transaction) => (
              <TableRow key={transaction.id} hover>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {transaction.transaction_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(transaction.timestamp).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {new Date(transaction.timestamp).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {transaction.item?.item_code}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {transaction.item?.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTransactionIcon(transaction.transaction_type)}
                    <Typography variant="body2">
                      {formatTransactionType(transaction.transaction_type)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    color={transaction.quantity >= 0 ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                    variant="body2"
                  >
                    {transaction.quantity >= 0 ? '+' : ''}{transaction.quantity}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    £{parseFloat(transaction.unit_cost || 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    £{parseFloat(transaction.total_value || 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {transaction.employee?.user?.username || 
                     transaction.employee?.employee_code || 
                     'System'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {transaction.reference_type && transaction.reference_id && (
                    <Typography variant="body2" color="primary">
                      {transaction.reference_type} #{transaction.reference_id}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {transaction.notes}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {!loading && data.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            {(searchTerm || filterType) 
              ? 'No transactions match your search criteria' 
              : 'No transactions found for the selected period'
            }
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {Math.ceil(totalCount / pageSize) > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination 
            count={Math.ceil(totalCount / pageSize)} 
            page={page}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            disabled={loading}
          />
        </Box>
      )}
    </Box>
  );
}

// Stock Adjustment Dialog
function StockAdjustmentDialog({ open, onClose, item, onSubmit }) {
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation - only quantity is required
    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    const adjustmentQuantity = adjustmentType === 'add' ? 
      parseFloat(quantity) : -parseFloat(quantity);
    
    onSubmit({
      quantity: adjustmentQuantity,
      reason: reason || `Stock ${adjustmentType === 'add' ? 'addition' : 'removal'} via inventory management`
    });
    
    // Reset form
    setQuantity('');
    setReason('');
    setAdjustmentType('add');
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          Adjust Stock for {item.item.item_code}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Current Stock: {item.quantity_on_hand} {item.item.unit_of_measure}
            </Typography>
          </Box>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Adjustment Type</InputLabel>
            <Select
              value={adjustmentType}
              onChange={(e) => setAdjustmentType(e.target.value)}
              label="Adjustment Type"
            >
              <MenuItem value="add">Add Stock (Receipt)</MenuItem>
              <MenuItem value="remove">Remove Stock (Usage/Loss)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Quantity *"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            required
            sx={{ mb: 2 }}
            inputProps={{ min: 0.01, step: 0.01 }}
            helperText="Required: Enter the adjustment quantity"
          />

          <TextField
            fullWidth
            label="Reason for Adjustment (Optional)"
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Cycle count adjustment, Goods received, Material usage..."
            helperText="Optional: Provide a reason for the stock adjustment"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Apply Adjustment
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}